import moment from 'moment';
import 'moment/locale/pt-br';
import ehDiaUtil from '@lfreneda/eh-dia-util';
import { BaseTerritoryData } from 'services/basesServices';
import { DayItem, DayStatus } from '../../../design-system/WeekCalendar';

type LegacyStatus = 'em_rota' | 'fora_de_rota' | 'indisponivel';

const LEGACY_TO_DS: Record<LegacyStatus, DayStatus> = {
  em_rota: 'route',
  fora_de_rota: 'off-route',
  indisponivel: 'unavailable',
};

const capitalizeWeekday = (day: moment.Moment): string => {
  const name = day.locale('pt-br').format('dddd').split('-')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const buildDayItems = (
  baseTerritory: BaseTerritoryData | null | undefined,
  weekStart: moment.MomentInput,
  now: moment.Moment = moment()
): DayItem[] => {
  const today = now.clone();

  const isWeekend = (day: moment.Moment): boolean =>
    day.isoWeekday() === 6 || day.isoWeekday() === 7;

  const isHoliday = (day: moment.Moment): boolean =>
    !ehDiaUtil(day.format('YYYY-MM-DD'));

  const isAfter90Days = (day: moment.Moment): boolean =>
    day.isAfter(today.clone().add(90, 'days'), 'day');

  const isParamNewRulesActive = (): boolean =>
    baseTerritory?.useNewRefuelRule || false;

  const isRouteDay = (day: moment.Moment): boolean => {
    switch (day.isoWeekday()) {
      case 1:
        return !!baseTerritory?.monday;
      case 2:
        return !!baseTerritory?.tuesday;
      case 3:
        return !!baseTerritory?.wednesday;
      case 4:
        return !!baseTerritory?.thursday;
      case 5:
        return !!baseTerritory?.friday;
      case 6:
        return !!baseTerritory?.saturday;
      case 7:
        return !!baseTerritory?.sunday;
      default:
        return false;
    }
  };

  const isTomorrow = (day: moment.Moment): boolean =>
    day.isSame(today.clone().add(1, 'day'), 'day');

  const isCurrentMonth = (day: moment.Moment): boolean =>
    day.isSame(today, 'month');

  const isSaturday = (day: moment.Moment): boolean => day.isoWeekday() === 6;
  const isFriday = (day: moment.Moment): boolean => day.isoWeekday() === 5;
  const getNextMonday = (): moment.Moment => today.clone().isoWeekday(8);
  const isTodaySaturday = (): boolean => today.isoWeekday() === 6;
  const isTodaySunday = (): boolean => today.isoWeekday() === 7;

  const isStopHourOperation = (): boolean => {
    const disableOperation = isParamNewRulesActive()
      ? moment('12:00pm', 'h:mma')
      : moment('16:00pm', 'h:mma');
    return today.clone().isAfter(disableOperation);
  };

  const getSugestDay = (): moment.Moment => {
    let qtdDaysControl =
      isStopHourOperation() || isWeekend(today) || isHoliday(today) ? 4 : 3;

    let newDay = today.clone();
    for (let i = 1; i <= qtdDaysControl; i += 1) {
      if (
        !today.isSame(newDay, 'day') &&
        isHoliday(newDay) &&
        !isWeekend(newDay)
      ) {
        qtdDaysControl += 1;
      }
      if (!today.isSame(newDay, 'day') && isWeekend(newDay)) {
        qtdDaysControl += 1;
      }
      newDay = newDay.add(1, 'days');
    }

    while (isWeekend(newDay) || isHoliday(newDay)) {
      newDay = newDay.add(1, 'days');
    }
    return newDay.clone();
  };

  const isBeforeSugestDay = (day: moment.Moment): boolean =>
    day.isBefore(getSugestDay(), 'day');

  const startOfWeek = moment(weekStart).clone().startOf('isoWeek');
  const items: DayItem[] = [];

  for (let i = 0; i < 7; i += 1) {
    const day = startOfWeek.clone().add(i, 'days');
    let status: LegacyStatus = 'fora_de_rota';

    if (day.isSameOrBefore(today, 'day')) {
      status = 'indisponivel';
    } else if (isAfter90Days(day)) {
      status = 'indisponivel';
    } else if (isHoliday(day) && day.isoWeekday() !== 6) {
      status = 'indisponivel';
    } else if (day.isoWeekday() === 7) {
      status = 'indisponivel';
    } else if (isBeforeSugestDay(day) && !isRouteDay(day)) {
      status = 'indisponivel';
    } else if (!isRouteDay(day)) {
      status = 'fora_de_rota';
    } else {
      status = 'em_rota';
    }

    if (isParamNewRulesActive()) {
      if (isBeforeSugestDay(day)) {
        if (!isRouteDay(day)) {
          status = 'indisponivel';
        } else if (isTomorrow(day) && (isWeekend(today) || isHoliday(today))) {
          status = 'indisponivel';
        } else if (
          (day.format('dddd').toLowerCase() === 'segunda-feira' ||
            day.format('dddd').toLowerCase() === 'domingo') &&
          (isWeekend(today) || isHoliday(today))
        ) {
          status = 'indisponivel';
        } else if (isTomorrow(day) && isStopHourOperation()) {
          status = 'indisponivel';
        } else if (isHoliday(today)) {
          status = 'indisponivel';
        }
      }

      if (isHoliday(day) && day.format('dddd').toLowerCase() !== 'sábado') {
        status = 'indisponivel';
      }

      if (!isRouteDay(day) && day.format('dddd').toLowerCase() === 'sábado') {
        status = 'indisponivel';
      }

      if (
        isTodaySaturday() &&
        baseTerritory?.monday === true &&
        day.isSame(getNextMonday(), 'day') &&
        isStopHourOperation() === true &&
        status === 'em_rota' &&
        isCurrentMonth(day)
      ) {
        status = 'fora_de_rota';
      }

      if (
        isTodaySunday() &&
        baseTerritory?.monday === true &&
        day.isSame(getNextMonday(), 'day') &&
        status === 'em_rota' &&
        isCurrentMonth(day)
      ) {
        status = 'fora_de_rota';
      }

      if (
        isFriday(today) &&
        baseTerritory?.saturday === true &&
        isSaturday(day) &&
        isStopHourOperation() &&
        status === 'em_rota'
      ) {
        status = 'fora_de_rota';
      }
    }

    items.push({
      date: day.format('YYYY-MM-DD'),
      weekdayLabel: capitalizeWeekday(day),
      dayNumber: day.date(),
      status: LEGACY_TO_DS[status],
      isToday: day.isSame(today, 'day'),
    });
  }

  return items;
};

export default buildDayItems;
